/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useMemo } from 'react';

import Styled from './styles';
import colors from '../../assets/colors';
import CustomScrollbar from '../custom-scrollbar';
import { Typography } from '../typography';
import Chart, { barColors } from './components/chart';
import { formatToCurrency } from '../../core/helpers/numberFormatter';

const StackBars: FC<any> = props => {
	let { data, isPadding, grouping } = props;
	const switchedData = useMemo(() => data?.map((item: any) => {
		const { level1, level2 } = item;
		return { ...item, level1: level2, level2: level1 };
	}), [data]);

	const chooseSort: string = useMemo(() => {
		if (!switchedData || !switchedData?.length) return 'nosort';
		const checkTypeValue = switchedData[0].level2;
		if (!isNaN(checkTypeValue)) return 'number';
		const checkDateValue = new Date(checkTypeValue);
		if (checkDateValue?.getTime()) return 'date';
		return 'string'
	}, [switchedData]);

	const sortedData = useMemo(() => (switchedData && switchedData.length) 
		? switchedData?.sort((a: any, b: any) => {
			if (chooseSort === 'number') return a?.level2 - b?.level2;
			if (chooseSort === 'date') return new Date(String(a?.level2)).getTime() - new Date(String(b?.level2)).getTime();
			return a?.level2?.localeCompare(b?.level2);
		}) 
		: []
	, [switchedData, chooseSort]);
	
	const columnsLength = [...new Set(sortedData.map((i: any) => i.level2))]
		.length;
	const columns = [...new Set(sortedData.map((i: any) => i.level2))];

	const uniqueItems: any = useMemo(() => {
		return [...new Set(sortedData.map((i: any) => i.level1))].reduce(
			(acc: any, v: any, i: number) => ({
				...acc,
				[v]: barColors[i] || colors.grey,
			}),
			{}
		);
	}, [sortedData]);

	const chartData = useMemo(() => {
		return sortedData.reduce(
			(acc: any, { level1, level2, count }: any, i: number) => {
				const label = level1;
				if (!acc[label]) {
					return {
						...acc,
						[label]: [
							{
								_id: level2,
								count,
								color: uniqueItems[label],
							},
						],
					};
				}
				return {
					...acc,
					[label]: [
						...acc[label],
						{
							_id: level2,
							count,
							color: uniqueItems[label],
						},
					],
				};
			},
			{}
		);
	}, [sortedData]);

	const getSum = (row: any[]) => {
		if (!row) return '';
		return row.reduce(add, 0);
	};

	const add = (accumulator: any, a: any) => {
		return accumulator + a.count;
	};

	const finalData = Object.keys(chartData);
	if (!finalData.length) return null;

	const oneBarWidth = 140;
	const chartWidth = Math.max(columnsLength * oneBarWidth, 320);
	const updatedChartData: any = {};
	finalData.forEach((category: string) => {
		const currentPart = chartData[category];
		const chartPart = columns.map((column: any) => ({
			_id: (!isNaN(column) && grouping?.groupingLevel1 === 'Amount') ? formatToCurrency(+column) : column,
			color: currentPart[0].color ?? '#fff',
			count: currentPart?.find((item: any) => item._id === column)?.count ?? 0,
		}))
		updatedChartData[category] = chartPart;
	});

	return (
		<div>
			<CustomScrollbar width={chartWidth}>
				<Chart
					{...props}
					chartWidth={chartWidth}
					chartData={updatedChartData}
					finalData={finalData}
				/>
			</CustomScrollbar>
			<div style={{ 
				position: 'absolute', 
				margin: '10px 0px 20px 50px',
				 }}>
				
			<Typography> {grouping?.groupingLevel2} </Typography> 
			</div>
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					flexWrap: 'wrap',
					alignItems: 'center',
					padding: isPadding ? 0 : 20,
					marginTop: 30,
				}}
			>
				{[
					...[...finalData].splice(0, barColors.length),
					finalData.length > barColors.length ? 'Other' : null,
				].map((item, index) =>
					item ? (
						<div
							key={`stackBar${index}${item}`}
							style={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								marginBottom: 6,
								marginLeft: 30,
								width:180,
							}}
						>
							<Styled.Circle
								style={{
									backgroundColor: barColors[index] || colors.grey,
								}}
							/>
							<div style={{ paddingRight: 20 }}>
								<Typography numberOfLines={1} color="black" size="tiny">
									{`${(grouping?.groupingLevel2?.toLowerCase() === 'amount' && !isNaN(+item)) 
											? formatToCurrency(+item) 
											: item
										} ${chartData[item] ? '-' : ''} ${getSum(chartData[item])}`
									}
								</Typography>
							</div>
						</div>
					) : null
				)}
			</div>
		</div>
	);
};

export default React.memo(StackBars);
